// ====================================================================
// VILLAGE DECOR — authored layout enrichment for the central hub
// ====================================================================
// The village hub's "lived-in" layer, layered on top of the hand-placed huts/
// well/paths in main.js without moving any of them:
//   1. a town-square centrepiece — a maypole (bump it for a hello) that gives
//      the empty spawn plaza a focal point;
//   2. the Village Green — a cozy picnic terminus for the south road, which
//      used to dead-end in fog while the other three cardinals reach a zone;
//   3. junction "gardens" that fill the gaps the radial paths used to converge
//      on; and
//   4. a few named landmarks (Lookout Rock, Old Orchard, Berry Thicket) that
//      turn the outer scatter ring into something that reads as authored.
//
// Like the zones (src/world/zones/*) this is CONTENT, so it lives out here and
// receives every builder it needs through a one-directional ctx from main.js —
// it never reaches back into core globals. The work is overwhelmingly instanced
// (buildTree / buildBush / buildFlowerPatch / prop clusters join existing
// per-chunk InstancedMesh pools, ~0 draw calls); the few non-instanced pieces
// (the maypole and ribbons, the picnic table, two signposts) are deliberately
// minimal to respect the ~150 draw-call Chromebook floor, and the hard-coded-Y
// pieces sit on the dead-flat village plateau / south-road centreline. See the
// plan and BUILD.md for the budget reasoning.
import * as THREE from 'three';
import { PALETTE, ACCENTS } from '../core/palette.js';

// ---- 1. town-square centrepiece -------------------------------------------

// A maypole marking the town square. When the kit loaded, this is the handmade
// GLB maypole (an instanced chunk prop — 0 extra draw calls, grounded via the
// sampled ground height). Decorative (non-collidable) either way, so it never
// walls off the to-well path it stands beside. Falls back to the procedural
// pole + ribbons (a slim pole crowned with a fan of vertex-coloured ribbons
// drawn as ONE merged mesh) when the kit is missing, so an offline first visit
// still gets a centrepiece.
function buildMaypole(ctx, x, z) {
  if (ctx.kitReady && ctx.kitReady()) {
    ctx.addProp('maypole', { x, z });
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const fx = x + Math.cos(a) * 0.72;
      const fz = z + Math.sin(a) * 0.72;
      const h = 0.18 + (i % 2) * 0.04;
      ctx.addProp('flowerStem', { x: fx, z: fz, h });
      ctx.addProp('flowerHead', { x: fx, z: fz, h, s: 0.58, color: ACCENTS[i % ACCENTS.length] });
    }
    return;
  }
  const poleH = 2.2;
  ctx.addCyl(x, poleH / 2, z, 0.1, poleH, PALETTE.woodLight, false);  // slim pole, no collision
  ctx.addSphere(x, poleH + 0.08, z, 0.16, PALETTE.goldSoft, false);    // warm topper focal point

  const RIBBONS = 8;
  const top = poleH - 0.06;
  const baseR = 0.55, baseY = 0.6;      // ribbons drape out to waist-height ends
  const positions = [];
  const cols = [];
  const c = new THREE.Color();
  for (let i = 0; i < RIBBONS; i++) {
    const a = (i / RIBBONS) * Math.PI * 2;
    const a2 = a + 0.18;                 // ribbon width at the draped end
    const e1x = x + Math.cos(a) * baseR,  e1z = z + Math.sin(a) * baseR;
    const e2x = x + Math.cos(a2) * baseR, e2z = z + Math.sin(a2) * baseR;
    positions.push(x, top, z, e1x, baseY, e1z, e2x, baseY, e2z);
    c.set(ACCENTS[i % ACCENTS.length]);
    for (let v = 0; v < 3; v++) cols.push(c.r, c.g, c.b);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(cols, 3));
  const ribbons = new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: true })
  );
  ctx.propsGroup.add(ribbons);
}

function buildCentrepiece(ctx) {
  // Offset SOUTH of spawn (0,0), between the player and the well, so it stays
  // out of the +Z camera throat and off the spawn tile.
  buildMaypole(ctx, 0, -1.6);
  ctx.buildFlowerPatch(0, -1.6, 8, 1.0, [PALETTE.coral, PALETTE.gold, PALETTE.lavender]);                 // bright ring at the base
  // Market-day clutter ringing the pole — kept off the x=0 to-well path.
  ctx.buildTownPropCluster(0, -1.6, [
    [1.2, -0.3, ctx.buildMarketBasket, 0.85, 0.3],
    [-1.2, -0.3, ctx.buildCrate, 0.8, -0.4],
    [1.0, 0.7, ctx.buildBarrel, 0.8, 0.5],
    [-1.0, 0.7, ctx.buildFlowerBox, 0.8, 0.2],
  ], 0);

  // The maypole doubles as the town-square marker: bump it for a friendly hello
  // (no separate notice-board mesh, to stay inside the draw-call budget).
  ctx.addVillageLandmark(0, -1.6, 1.6);
  ctx.addBumpZone(0, -1.6, 1.0, 'town-square', sourceId =>
    ctx.showBumpMsg('THE TOWN SQUARE', 'The heart of Chockabloc — say hello! 🎀', 4, sourceId));
}

// ---- 2. the Village Green (south destination) -----------------------------

function buildVillageGreen(ctx) {
  const CX = 0, CZ = -24;
  // Extend the road from its old fog terminus into the green so it reads as
  // arriving somewhere; buildPath also registers safe-checkpoint segments, so a
  // kid who wanders south respawns at the green rather than back at spawn.
  ctx.buildPath([[0, -27], [0, -24.5], [0, -22]]);

  // A loose orchard + berry bushes framing the clearing. Trees and bushes are
  // already present in this (south) chunk via the road tree-lines and scatter,
  // so they join the existing instanced meshes and add ~0 draw calls — the
  // budget-friendly way to dress a destination 24u out yet in the spawn frustum.
  ctx.buildTree(-5, -21, 1.1);
  ctx.buildTree(-6, -26, 1.2);
  ctx.buildTree(5.2, -21.5, 1.0);
  ctx.buildTree(6, -27, 1.15);
  ctx.buildTree(-4.5, -28, 0.95);
  ctx.buildTree(4, -28.5, 1.05);
  ctx.buildBush(-2.6, -27, 1.2);
  ctx.buildBush(2.8, -27.5, 1.1);
  ctx.buildBush(-3.4, -22.3, 1.0);
  ctx.buildBush(3.4, -22.3, 1.0);

  // A lean picnic table just off the centreline (the green plateau is flat, so
  // the hard-coded Y sits correctly without sampling). Top + two trestle legs.
  const tx = -1.8, tz = -24.5;
  ctx.addBox(tx, 0.55, tz, 1.3, 0.06, 0.7, PALETTE.woodLight);        // top
  ctx.addBox(tx - 0.55, 0.27, tz, 0.08, 0.5, 0.6, PALETTE.woodDark);  // leg
  ctx.addBox(tx + 0.55, 0.27, tz, 0.08, 0.5, 0.6, PALETTE.woodDark);  // leg

  // Welcome message + a signpost pointing home.
  ctx.addVillageLandmark(CX, CZ, 2.6);
  ctx.addBumpZone(CX, CZ, 1.8, 'village-green', sourceId =>
    ctx.showBumpMsg('THE VILLAGE GREEN', 'A sunny spot for a picnic. 🧺', 4, sourceId));
  ctx.buildSignpost(1.7, -21, [{ text: 'CHOCKABLOC', yaw: 0.15, flip: true }],
    'SIGNPOST: HOME', 'signpost-home-south');
}

// ---- 3. junction dwell spots ----------------------------------------------
// Coords are kept ≥1.5u off every hut AABB and out of the camera throat; the
// non-instanced pieces (benches, stall, lanterns) sit at the plaza sides/south
// so most are frustum-culled at the spawn frame. Instanced beats are free.

function buildDwellSpots(ctx) {
  // The core village is already dense and tight on the draw-call budget, so the
  // junction "gardens" are instanced-only (flower beds + clutter that read as
  // tended garden plots and informal crate "stools", ~0 draw calls) — no
  // non-instanced benches/lanterns added in the spawn frame.
  ctx.buildFlowerPatch(6.0, 3.0, 6, 0.7);
  ctx.buildTownPropCluster(5.6, 2.4, [
    [0.6, -0.5, ctx.buildCrate, 0.85, 0.1],
    [0.9, -0.6, ctx.buildGrassTuft, 0.8, 0],
    [-0.7, 0.8, ctx.buildMarketBasket, 0.75, 0.4],
  ], 0);

  // West garden nook — flowers + crate "stools".
  ctx.buildFlowerPatch(-6.0, 3.0, 6, 0.7);
  ctx.buildTownPropCluster(-5.6, 2.4, [
    [0.6, -0.4, ctx.buildCrate, 0.85, 0.2],
    [-0.5, 0.3, ctx.buildBarrel, 0.8, -0.3],
    [0.9, 0.7, ctx.buildGrassTuft, 0.8, 0],
  ], 0);

  // SE produce nook — a market-day cluster (no built stall; instanced only).
  ctx.buildTownPropCluster(3.0, -5.6, [
    [-0.6, 0.3, ctx.buildMarketBasket, 0.85, 0.3],
    [0.7, 0.2, ctx.buildCrate, 0.85, -0.3],
    [0.1, 0.9, ctx.buildBarrel, 0.8, 0],
    [-0.9, -0.4, ctx.buildTinyFlag, 0.9, 0],
  ], 0);

  // SW garden beat — instanced only.
  ctx.buildFlowerPatch(-3.0, -5.6, 6, 0.7);
  ctx.buildTownPropCluster(-3.0, -5.6, [
    [0.8, 0.3, ctx.buildBarrel, 0.8, 0.3],
    [-0.6, 0.5, ctx.buildMarketBasket, 0.75, -0.4],
    [0.3, -0.8, ctx.buildGrassTuft, 0.8, 0],
  ], 0);
}

// ---- 4. outer-ring landmark POCKETS ---------------------------------------
// The four diagonal roads used to end at static prop piles — a kid who followed
// one was rewarded with a rock. Each terminus is now a small discoverable pocket
// with a payoff: a coin cache and a bump message that names the place, so the
// diagonals lead somewhere worth walking to. The SW diagonal, which used to have
// nothing at all, gets a fishing-hole pond (the map's second body of water).
// The clusters stay in the 12–24u ring (never in the spawn frustum) and remain
// overwhelmingly instanced; the coins and one pond are the only additions.

function buildOuterLandmarks(ctx) {
  // Lookout Rock (NE) — a stack of boulders you clamber for a coin cache and a
  // view. A little cairn-topper coin sits on the highest rock.
  ctx.buildRock(21, 16, 1.5);
  ctx.buildRock(21.9, 15.4, 1.1);
  ctx.buildRock(20.3, 16.6, 1.0);
  ctx.buildRock(21.4, 16.8, 0.8);
  ctx.buildMushroomCluster(19.8, 17.0, 3);
  ctx.buildGrassTuft?.(22.3, 16.2, 0.7);
  ctx.buildSignpost(18.6, 15.0, [{ text: 'LOOKOUT', yaw: -2.35 }],
    'SIGNPOST: LOOKOUT', 'signpost-lookout');
  ctx.addCoinAt?.(22.6, 16.6);                     // a coin tucked beside the stack
  ctx.addCoinRing?.(20.8, 16, 1.6, 4, 0.9);
  ctx.addVillageLandmark?.(21, 16, 2.2);
  ctx.addBumpZone?.(21, 16, 1.8, 'lookout-rock', sourceId =>
    ctx.showBumpMsg('LOOKOUT ROCK 🪨', 'The best view in Chockytown — can you spot every zone?', 4, sourceId));

  // Old Orchard (NW) — a tight ring of apple trees dropping windfall coins, plus
  // a flower bed (a butterfly/firefly habitat for the road out).
  ctx.buildTree(-19, 20, 1.2);
  ctx.buildTree(-21, 19, 1.1);
  ctx.buildTree(-20.5, 21, 1.0);
  ctx.buildTree(-18.5, 21.4, 1.15);
  ctx.buildTree(-21.4, 20.8, 0.95);
  ctx.buildFlowerPatch(-20, 20, 7, 1.0);
  ctx.addCoinAt?.(-19.4, 20.2);                     // windfall under the trees
  ctx.addCoinAt?.(-20.8, 19.4);
  ctx.addCoinAt?.(-20.2, 21.2);
  ctx.addVillageLandmark?.(-20, 20, 2.4);
  ctx.addBumpZone?.(-20, 20, 1.9, 'old-orchard', sourceId =>
    ctx.showBumpMsg('THE OLD ORCHARD 🍎', 'Shake a tree and the apples come raining down!', 4, sourceId));

  // Berry Thicket (SE) — a dense bush maze hiding a coin cache in its heart.
  ctx.buildBush(22, -18, 1.4, true);
  ctx.buildBush(22.9, -17.3, 1.2, true);
  ctx.buildBush(21.2, -18.7, 1.3, true);
  ctx.buildBush(22.6, -18.9, 1.0, true);
  ctx.buildBush(20.9, -17.5, 1.1, true);
  ctx.buildMushroomCluster(23.4, -18.2, 4);
  ctx.buildStarAccent?.(21.8, -17.6, 0.55, 0.2);
  ctx.addCoinRing?.(21.9, -18, 1.3, 5, 0.85);       // hidden in the brambles
  ctx.addVillageLandmark?.(21.9, -18, 2.0);
  ctx.addBumpZone?.(21.9, -18, 1.7, 'berry-thicket', sourceId =>
    ctx.showBumpMsg('THE BERRY THICKET 🫐', 'Sweet berries hide sweeter treasure — dig in!', 4, sourceId));

  // Fishing Hole (SW) — the diagonal that led NOWHERE now has a little pond with
  // a plank dock, reeds and a coin cache, plus the map's second body of water.
  if (ctx.buildFishingHole) {
    ctx.buildFishingHole(-22, -20, 2.6);
    ctx.buildDock?.(-20.1, -18.6, -0.8, 2.2);       // a pier out over the water
    ctx.buildLantern?.(-19.4, -18.0, false);
    ctx.buildGrassTuft?.(-24.2, -21.4, 0.8);
    ctx.buildGrassTuft?.(-23.6, -18.4, 0.7);
    ctx.buildLilyPad?.(-22.6, -20.4, 0.9, 0.4);
    ctx.buildLilyPad?.(-21.4, -19.5, 0.8, 1.6);
    ctx.buildRock(-24.6, -19.6, 0.9);
    ctx.buildMushroomCluster(-24.0, -22.2, 3);
    ctx.addCoinRing?.(-20.4, -18.9, 1.0, 3, 0.9);   // coins along the dock
    ctx.addVillageLandmark?.(-22, -20, 2.6);
    ctx.addBumpZone?.(-20.6, -18.8, 1.8, 'fishing-hole', sourceId =>
      ctx.showBumpMsg('THE FISHING HOLE 🎣', 'A quiet pond off the west path. What lives down there?', 4, sourceId));
  }
}

// ---- entry point ----------------------------------------------------------

export function buildVillageDecor(ctx) {
  buildCentrepiece(ctx);
  buildVillageGreen(ctx);
  buildDwellSpots(ctx);
  buildOuterLandmarks(ctx);
}
